import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import reportTemplatesService from "../../../services/reportTemplatesService";
import { TEMPLATE_COLUMNS, TEMPLATE_SORT_KEYS } from "./reportColumnCatalog";

const FILTER_LABELS = {
  status: "Status",
  leadType: "Lead Type",
  leadSource: "Lead Source",
  serviceDashboard: "Dashboard",
  salesRep: "Sales Rep",
  category: "Category",
  nextContact: "Next Contact",
  search: "Search",
};

function extractFilename(cd, fallback) {
  if (!cd) return fallback;
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  return m ? decodeURIComponent(m[1].replace(/"/g, "")) : fallback;
}

function triggerBlobDownload(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function fmtFilterValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function activeFilterEntries(filters = {}) {
  const out = [];
  for (const [k, v] of Object.entries(filters)) {
    if (["dateRange", "dateField", "dateFrom", "dateTo"].includes(k)) {
      if (k === "dateRange" && v && v !== "all") out.push(["Range", v]);
      continue;
    }
    if (v === undefined || v === null || v === "" || v === "all") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out.push([FILTER_LABELS[k] || k, fmtFilterValue(v)]);
  }
  return out;
}

function columnRowsFromPayload(payload) {
  const vc = payload?.visibleColumns;
  const order = Array.isArray(payload?.columnOrder) ? payload.columnOrder : [];
  const labels = payload?.columnLabels || {};

  if (!vc || typeof vc !== "object") return [];

  const fromOrder = order.filter((k) => vc[k]);
  const extras = Object.keys(vc).filter(
    (k) => vc[k] && !fromOrder.includes(k),
  );
  const keys = [...fromOrder, ...extras];

  return keys
    .map((vk) => {
      const def = TEMPLATE_COLUMNS.find((c) => c.viewKey === vk);
      if (!def) return null;
      return { key: vk, group: def.group, label: labels[vk] || def.label };
    })
    .filter(Boolean);
}

function sortSummary(sortState = []) {
  if (!Array.isArray(sortState) || sortState.length === 0) return [];
  return sortState.map((s, i) => {
    const key = TEMPLATE_SORT_KEYS.find((k) => k.key === s.col);
    const dir = s.dir === "desc" ? "▼" : "▲";
    return `${key?.label || s.col} ${dir}${i > 0 ? ` (${i + 1})` : ""}`;
  });
}

export default function ReportTemplateViewPanel({
  templateId,
  onClose,
  onEdit,
  onDeleted,
}) {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await reportTemplatesService.get(templateId);
        if (!cancelled) setTpl(data);
      } catch {
        if (!cancelled) setError("Failed to load template");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const showFlash = (msg) => {
    setFlash(msg);
    if (showFlash._t) clearTimeout(showFlash._t);
    showFlash._t = setTimeout(() => setFlash(""), 2200);
  };

  const handleDownload = async (format) => {
    if (!tpl) return;
    setBusy(format);
    try {
      const { blob, contentDisposition } =
        await reportTemplatesService.downloadSample(tpl.id, format);
      const fallback = `${(tpl.name || "template").replace(/\s+/g, "_")}_sample.${format}`;
      const name = extractFilename(contentDisposition, fallback);
      triggerBlobDownload(blob, name);
      showFlash(`Downloaded "${name}"`);
    } catch {
      setError("Failed to download sample");
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!tpl) return;
    setBusy("delete");
    setDeleteError("");
    try {
      await reportTemplatesService.remove(tpl.id);
      onDeleted?.(tpl.id);
    } catch (e) {
      const data = e?.response?.data;
      if (data?.error === "TEMPLATE_IN_USE") {
        setDeleteError(data.message || "Template is in use.");
      } else {
        setDeleteError("Failed to delete template.");
      }
    } finally {
      setBusy(null);
    }
  };

  const payload = tpl?.payload || {};
  const columns = columnRowsFromPayload(payload);
  const quickChips = Array.isArray(payload.activeQuickChips)
    ? payload.activeQuickChips
    : [];
  const filterEntries = activeFilterEntries(payload.filters || {});
  const sorts = sortSummary(payload.sortState);

  const panel = (
    <div className="lead-panel-overlay" onMouseDown={onClose}>
      <aside
        className="lead-panel"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Template ${tpl?.name || ""}`}
      >
        <div className="lead-panel-top">
          <div>
            <div className="lead-panel-eyebrow">Report Template</div>
            <h2>{tpl?.name || "Template"}</h2>
            <div className="lead-panel-subtitle">
              {columns.length || "—"} column
              {columns.length === 1 ? "" : "s"}
              {tpl?.is_default ? " · Default ★" : ""}
            </div>
          </div>
          <button
            type="button"
            className="lead-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {flash && (
          <div className="r6-tpl-flash" style={{ margin: "0 24px 8px" }}>
            <i className="fas fa-check-circle" /> {flash}
          </div>
        )}
        {error && (
          <div className="error-message" style={{ margin: "0 24px 8px" }}>
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        )}

        <div className="lead-panel-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onEdit?.(tpl.id)}
            disabled={!tpl}
          >
            <i className="fas fa-edit" /> Edit Template
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => handleDownload("xlsx")}
            disabled={busy === "xlsx" || !tpl}
          >
            <i
              className={`fas ${
                busy === "xlsx" ? "fa-spinner fa-spin" : "fa-file-excel"
              }`}
            />{" "}
            Excel sample
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => handleDownload("csv")}
            disabled={busy === "csv" || !tpl}
          >
            <i
              className={`fas ${
                busy === "csv" ? "fa-spinner fa-spin" : "fa-file-csv"
              }`}
            />{" "}
            CSV sample
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              setDeleteConfirm(true);
              setDeleteError("");
            }}
            disabled={!tpl}
          >
            <i className="fas fa-trash" /> Delete
          </button>
        </div>

        {deleteConfirm && (
          <section className="lead-panel-section">
            <div className="r6-se-panel-confirm r6-se-panel-confirm-danger">
              <div className="r6-se-panel-confirm-msg">
                Permanently delete <strong>{tpl?.name}</strong>? This cannot be
                undone.
              </div>
              {deleteError && (
                <div className="r6-se-panel-confirm-msg" style={{ color: "#991b1b" }}>
                  {deleteError}
                </div>
              )}
              <div className="r6-se-panel-confirm-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setDeleteConfirm(false)}
                  disabled={busy === "delete"}
                >
                  Keep
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleDelete}
                  disabled={busy === "delete"}
                >
                  <i className="fas fa-trash" /> Delete permanently
                </button>
              </div>
            </div>
          </section>
        )}

        {loading ? (
          <div className="lead-panel-section">
            <div className="r6-se-muted">Loading…</div>
          </div>
        ) : !tpl ? null : (
          <>
            {(payload.title || payload.headerText) && (
              <section className="lead-panel-section">
                <div className="lead-panel-section-title">Header</div>
                {payload.title && (
                  <div className="r6-tpl-summary-title-line">
                    {payload.title}
                  </div>
                )}
                {payload.headerText && (
                  <div className="r6-tpl-summary-sub-line">
                    {payload.headerText}
                  </div>
                )}
              </section>
            )}

            <section className="lead-panel-section">
              <div className="lead-panel-section-title">Columns</div>
              {columns.length === 0 ? (
                <div className="lead-panel-empty">Default columns</div>
              ) : (
                <div className="r6-tpl-panel-cols">
                  {columns.map((c, i) => (
                    <div key={c.key} className="r6-tpl-panel-col-row">
                      <span className="r6-tpl-panel-col-num">{i + 1}</span>
                      <span className="r6-tpl-panel-col-label">{c.label}</span>
                      <span className="r6-tpl-panel-col-group">{c.group}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {quickChips.length > 0 && (
              <section className="lead-panel-section">
                <div className="lead-panel-section-title">Quick filters</div>
                <div className="r6-tpl-summary-chips">
                  {quickChips.map((k) => (
                    <span
                      key={k}
                      className="r6-tpl-chip r6-tpl-chip-quick"
                    >
                      {k.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {filterEntries.length > 0 && (
              <section className="lead-panel-section">
                <div className="lead-panel-section-title">Filters</div>
                <div className="lead-panel-grid">
                  {filterEntries.map(([k, v]) => (
                    <div key={k} className="lead-panel-detail">
                      <span className="lead-panel-detail-label">{k}</span>
                      <span className="lead-panel-detail-value">{v}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {sorts.length > 0 && (
              <section className="lead-panel-section">
                <div className="lead-panel-section-title">Sort</div>
                <div className="r6-tpl-summary-chips">
                  {sorts.map((s, i) => (
                    <span key={i} className="r6-tpl-chip r6-tpl-chip-sort">
                      {s}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {columns.length === 0 &&
              quickChips.length === 0 &&
              filterEntries.length === 0 &&
              sorts.length === 0 &&
              !payload.title &&
              !payload.headerText && (
                <section className="lead-panel-section">
                  <div className="lead-panel-empty">
                    Uses defaults — no explicit columns, filters, or sort.
                  </div>
                </section>
              )}
          </>
        )}
      </aside>
    </div>
  );

  return createPortal(panel, document.body);
}